import React, { useState } from 'react';
import {
  Box,
  Button,
  FormHelperText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import {
  useAddStoreQuery,
  useUpdateStoreOwnerByIdQuery,
} from '../Query/AddStoreQuery';
import { useThemeColors } from '../../hooks/useThemeColors';
import allForm from '../Stateless/Form';
import {
  MerchantSchema,
  CurrencyCode,
  MerchantStripeDetailsSchemaPublishStatusEnum,
  MerchantPayPalSchemaPublishStatusEnum,
  MerchantNetBankingSchemaPublishStatusEnum,
} from '../../client-sdk';
import { toast } from 'react-hot-toast';
import PhoneNumberInput from './Utils/PhoneNumberInput';
import { FieldInputProps, FormikProps } from 'formik';
import ImageUploadField from './Utils/ImageUploadField';
import LocationSearchInput from '../Stateless/LocationSearchInput';
import { createCancellationPolicyValidationSchema, splitPhoneNumber } from '../HelperFunctions/validationUtils';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  mobileNo: string;
  currencyCode: CurrencyCode;
  businessType: string;
  commission: string;
  useCommonCommission: string;
  cancelStatus: string;
  cancellationPolicy: string;
  cancellationPolicyArabic: string;
  idProof: { file: File | null; url: string } | null;
  restaurantLicense: { file: File | null; url: string } | null;
  stripePublishStatus: string;
  stripeClientId: string;
  stripeSecretId: string;
  paypalPublishStatus: string;
  paypalClientId: string;
  paypalSecretId: string;
  netBankingPublishStatus: string;
  bankName: string;
  branch: string;
  accountNo: string;
  ifscCode: string;
  location: string;
  country: string;
  state: string;
  city: string;
}

interface AddStoreOwnerProps {
  onClose?: () => void;
  storeOwner?: MerchantSchema | null;
}

interface CustomComponentProps {
  field: {
    name: string;
    value: any;
    onChange: (e: any) => void;
    onBlur: () => void;
  };
  form: {
    setFieldValue: (field: string, value: any) => void;
    setFieldTouched: (field: string, touched: boolean) => void;
    setFieldError: (field: string, message: string | undefined) => void;
    values: FormValues;
    touched: Record<string, boolean>;
    errors: Record<string, any>;
  };
}

interface CustomButtonProps {
  values: FormValues;
  setFieldValue: (field: string, value: any) => void;
  setFieldTouched: (field: string, touched: boolean) => void;
  errors: Record<string, any>;
  touched: Record<string, boolean>;
  resetForm: () => void;
  handleSubmit: () => void;
}

interface FileValue {
  file: File;
  url: string;
}

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .max(100, 'Name must be at most 100 characters')
    .matches(/^[A-Za-z\s]+$/, 'Name should only contain letters and spaces')
    .required('First Name is required')
    .test(
      'not-only-spaces',
      'Name cannot be only spaces',
      (value) => {
        if (typeof value !== 'string') return false;
        return value.trim().length > 0;
      }
    ),
  lastName: Yup.string()
    .max(100, 'Name must be at most 100 characters')
    .matches(/^[A-Za-z\s]+$/, 'Name should only contain letters and spaces')
    .required('Last Name is required')
    .test(
      'not-only-spaces',
      'Name cannot be only spaces',
      (value) => {
        if (typeof value !== 'string') return false;
        return value.trim().length > 0;
      }
    ),
  email: Yup.string().email('Invalid email').required('Email is required'),
  mobileNo: Yup.string()
    .required('Please enter the mobile number')
    .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => val?.length >= 9
    ),
  commission: Yup.number().when('useCommonCommission', {
    is: 'Custom commission',
    then: (schema) =>
      schema
        .moreThan(0, 'Commission must be greater than 0')
        .max(100, 'Commission cannot exceed 100%')
        .required('Commission is required'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  cancelStatus: Yup.string().required('Cancel status is required'),
  cancellationPolicy: Yup.string().when('cancelStatus', {
    is: 'Allow',
    then: () => createCancellationPolicyValidationSchema('Cancellation Policy'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  cancellationPolicyArabic: Yup.string().when('cancelStatus', {
    is: 'Allow',
    then: () =>
      createCancellationPolicyValidationSchema('Cancellation Policy (Arabic)'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  // accountNo: Yup.string()
  //   .matches(/^\d+$/, 'Account number must contain only digits')
  //   .min(5, 'Account number must be at least 5 digits')
  //   .max(34, 'Account number cannot exceed 34 digits')
  //   .required('Account number is required'),
  // ifscCode: Yup.string()
  //   .matches(
  //     /^[A-Z]{4}0[A-Z0-9]{6}$/,
  //     'IFSC code must be 11 characters long and follow the format: AAAA0XXXXXX'
  //   )
  //   .required('IFSC code is required'),
  idProof: Yup.mixed()
    .required('Id Proof is required')
    .test('fileSize', 'File size must be less than 500KB', function (value) {
      const fileValue = value as FileValue | null;
      if (!fileValue || !fileValue.file) return true;
      return fileValue.file.size <= 500000;
    })
    .test(
      'dimensions',
      'Image dimensions must be between 300x300px and 500x500px',
      function (value) {
        const fileValue = value as FileValue | null;
        if (!fileValue || !fileValue.file) return true;
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;
            img.onload = () => {
              const { width, height } = img;
              resolve(
                width >= 300 && height >= 300 && width <= 500 && height <= 500
              );
            };
            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(fileValue.file);
        });
      }
    ),
  restaurantLicense: Yup.mixed()
    .required('Restaurant License is required')
    .test('fileSize', 'File size must be less than 500KB', function (value) {
      const fileValue = value as FileValue | null;
      if (!fileValue || !fileValue.file) return true;
      return fileValue.file.size <= 500000;
    })
    .test(
      'dimensions',
      'Image dimensions must be between 300x300px and 500x500px',
      function (value) {
        const fileValue = value as FileValue | null;
        if (!fileValue || !fileValue.file) return true;
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;
            img.onload = () => {
              const { width, height } = img;
              resolve(
                width >= 300 && height >= 300 && width <= 500 && height <= 500
              );
            };
            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(fileValue.file);
        });
      }
    ),
   location: Yup.string().required('location is required')
     .test(
       'not-only-spaces',
       'Location cannot be only spaces',
       (value) => typeof value === 'string' && value.trim().length > 0
     ),
  country: Yup.string().required('Country is required'),
  state: Yup.string().required('State is required'),
  city: Yup.string().required('City is required'),
  stripeClientId: Yup.string().when('stripePublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema.required('Client ID is required when Stripe is published'),
  }),
  stripeSecretId: Yup.string().when('stripePublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema.required('Secret ID is required when Stripe is published'),
  }),
  paypalClientId: Yup.string().when('paypalPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema.required('Client ID is required when PayPal is published'),
  }),
  paypalSecretId: Yup.string().when('paypalPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema.required('Secret ID is required when PayPal is published'),
  }),
  bankName: Yup.string().when('netBankingPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema.required('Bank name is required when Net Banking is published'),
  }),
  branch: Yup.string().when('netBankingPublishStatus', {
    is: 'publish',
    then: (schema) =>
      schema.required('Branch is required when Net Banking is published'),
  }),
});


const EditStoreOwner: React.FC<AddStoreOwnerProps> = ({
  onClose,
  storeOwner,
}) => {
  const navigate = useNavigate();
  //  const [inputKey, setInputKey] = useState(0);
    const [address, setAddress] = useState('');

  // Initialize address state from storeOwner on mount or when storeOwner changes
  React.useEffect(() => {
    if (storeOwner?.address?.location) {
      setAddress(storeOwner.address.location);
    }
  }, [storeOwner]);
  const { mutate: addStoreOwner, isPending: isAdding } = useAddStoreQuery();
  const { mutate: updateStoreOwner, isPending: isUpdating } =
    useUpdateStoreOwnerByIdQuery();
  const { primary, warning } = useThemeColors();

  // const fullNumber = storeOwner?.mobileNo;
  // const countryCode = fullNumber?.slice(0, -10);

  const { countryCode,phone } = splitPhoneNumber(storeOwner?.mobileNo);
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCode);

  const isPending = storeOwner ? isUpdating : isAdding;
  const initialValues: FormValues = {
    firstName: storeOwner?.firstName || '',
    lastName: storeOwner?.lastName || '',
    email: storeOwner?.email || '',
    mobileNo: phone || '',
    currencyCode: storeOwner?.currencyCode || CurrencyCode.Usd,
    businessType: storeOwner?.businessType || 'Restaurant',
    commission: storeOwner?.commissionPercentage?.toString() || '',
    // useCommonCommission: storeOwner?.businessType || 'Use Common commission',
    useCommonCommission: storeOwner?.commissionPercentage != 0
      ? 'Custom commission'
      : 'Use Common commission',
    cancelStatus: storeOwner?.cancelStatus ? 'Allow' : 'Not Allow',
    cancellationPolicy: storeOwner?.cancellationPolicyEnglish || '',
    cancellationPolicyArabic: storeOwner?.cancellationPolicyArabic || '',
    idProof: storeOwner?.idProof
      ? { file: null, url: storeOwner.idProof }
      : null,
    restaurantLicense: storeOwner?.restaurantLicense
      ? { file: null, url: storeOwner.restaurantLicense }
      : null,
    stripePublishStatus:
      storeOwner?.paymentDetails?.stripe?.publishStatus ===
        MerchantStripeDetailsSchemaPublishStatusEnum.Publish
        ? 'publish'
        : 'unpublish',
    stripeClientId: storeOwner?.paymentDetails?.stripe?.clientId || '',
    stripeSecretId: storeOwner?.paymentDetails?.stripe?.secretId || '',
    paypalPublishStatus:
      storeOwner?.paymentDetails?.paypal?.publishStatus ===
        MerchantPayPalSchemaPublishStatusEnum.Publish
        ? 'publish'
        : 'unpublish',
    paypalClientId: storeOwner?.paymentDetails?.paypal?.clientId || '',
    paypalSecretId: storeOwner?.paymentDetails?.paypal?.secretId || '',
    netBankingPublishStatus:
      storeOwner?.paymentDetails?.netBanking?.publishStatus ===
        MerchantNetBankingSchemaPublishStatusEnum.Publish
        ? 'publish'
        : 'unpublish',
    bankName: storeOwner?.paymentDetails?.netBanking?.bankName || '',
    branch: storeOwner?.paymentDetails?.netBanking?.branch || '',
    accountNo: storeOwner?.paymentDetails?.netBanking?.accountNumber || '',
    ifscCode: storeOwner?.paymentDetails?.netBanking?.ifscode || '',
    location: storeOwner?.address?.location || '',
    country: storeOwner?.address?.country || '',
    state: storeOwner?.address?.state || '',
    city: storeOwner?.address?.city || '',
  };

  const handleSubmit = async (values: FormValues) => {
    console.log(storeOwner, 'storeOwner');
    const sendToServer = {
      id: storeOwner?.id || '64e4b7f2d7c8ab1f8b2f9c31',
      createdUserId: '64e4b7f2d7c8ab1f8b2f9c31',
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      // mobileNo: values.mobileNo,
      mobileNo: `${selectedCountryCode}${values.mobileNo}`,
      currencyCode: values.currencyCode,
      businessType: values.businessType,
      // commissionPercentage: Number(values.commission),
      commissionPercentage:
        values.useCommonCommission === 'Use Common commission'
          ? 0
          : Number(values.commission),
      cancelStatus: values.cancelStatus === 'Allow',
      cancellationPolicyEnglish: values.cancellationPolicy,
      cancellationPolicyArabic: values.cancellationPolicyArabic,
      idProof: typeof values.idProof === 'string' ? values.idProof : values.idProof?.url || '',
      restaurantLicense: typeof values.restaurantLicense === 'string' ? values.restaurantLicense : values.restaurantLicense?.url || '',
      address: {
        location: values.location,
        country: values.country,
        state: values.state,
        city: values.city,
      },
      paymentDetails: {
        stripe: {
          publishStatus:
            values.stripePublishStatus === 'publish'
              ? MerchantStripeDetailsSchemaPublishStatusEnum.Publish
              : MerchantStripeDetailsSchemaPublishStatusEnum.Unpublish,
          clientId: values.stripeClientId,
          secretId: values.stripeSecretId,
        },
        paypal: {
          publishStatus:
            values.paypalPublishStatus === 'publish'
              ? MerchantPayPalSchemaPublishStatusEnum.Publish
              : MerchantPayPalSchemaPublishStatusEnum.Unpublish,
          clientId: values.paypalClientId,
          secretId: values.paypalSecretId,
        },
        netBanking: {
          publishStatus:
            values.netBankingPublishStatus === 'publish'
              ? MerchantNetBankingSchemaPublishStatusEnum.Publish
              : MerchantNetBankingSchemaPublishStatusEnum.Unpublish,
          bankName: values.bankName,
          branch: values.branch,
          accountNumber: values.accountNo,
          ifscode: values.ifscCode,
        },
      },
      isActive: storeOwner?.isActive ?? true,
    };

    try {
      if (storeOwner?.id) {
        await updateStoreOwner(
          { id: storeOwner.id, data: sendToServer },
          {
            onSuccess: () => {
              toast.success('Store owner updated successfully');
              if (onClose) {
                onClose();
              } else {
                navigate('/store-owners');
              }
            },
            onError: (error: any) => {
              console.error('Error updating store owner:', error);
              toast.error(
                error.response?.data?.message || 'Failed to update store owner'
              );
            },
          }
        );
      } else {
        await addStoreOwner(sendToServer, {
          onSuccess: () => {
            toast.success('Store owner added successfully');
            if (onClose) {
              onClose();
            } else {
              navigate('/store-owners');
            }
          },
          onError: (error: any) => {
            console.error('Error adding store owner:', error);
            toast.error(
              error.response?.data?.message || 'Failed to add store owner'
            );
          },
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const allFields = [
    {
      name: 'firstName',
      label: 'First Name',
      type: 'text',
      isRequired: true,
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: 'text',
      isRequired: true,
    },
    {
      name: 'email',
      label: 'Email',
      type: 'text',
      isRequired: true,
    },
    {
      name: 'mobileNo',
      label: 'Mobile Number',
      type: 'custom',
      isRequired: true,
      component: ({
        field,
        form,
      }: {
        field: FieldInputProps<any>;
        form: FormikProps<any>;
      }) => (
        <PhoneNumberInput
          field={field}
          form={form}
          selectedCountryCode={selectedCountryCode}
          onCountryCodeChange={setSelectedCountryCode}
        />
      )
    },
    {
      name: 'password',
      label: 'Password',
      type: 'password',
      isRequired: true,
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      type: 'password',
      isRequired: true,
    },
    {
      name: 'currencyCode',
      label: 'Currency Code',
      type: 'text',
      value: '$',
      disabled: true,
    },
    {
      name: 'businessType',
      label: 'Business Type',
      type: 'radio',
      radioOptions: ['Restaurant', 'GroceryStore', 'PharmacyStore'],
      isRequired: true,
    },
    {
      name: 'useCommonCommission',
      label: 'Use Common commission',
      type: 'radio',
      radioOptions: ['Use Common commission', 'Custom commission'],
    },
    {
      name: 'commission',
      label: 'Commission In (%)',
      type: 'number',
      isRequired: true,
      dependsOn: { field: 'useCommonCommission', value: 'Custom commission' },
    },
    {
      name: 'cancelStatus',
      label: 'Cancel Status',
      type: 'radio',
      radioOptions: ['Allow', 'Not Allow'],
      isRequired: true,
    },
    {
      name: 'cancellationPolicy',
      label: 'Cancellation Policy',
      type: 'text',
      isRequired: true,
      dependsOn: { field: 'cancelStatus', value: 'Allow' },
      component: ({ field }: CustomComponentProps) => (
        <textarea
          {...field}
          rows={4}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
        />
      ),
    },
    {
      name: 'cancellationPolicyArabic',
      label: 'Cancellation Policy (In Arabic)',
      type: 'text',
      isRequired: true,
      dependsOn: { field: 'cancelStatus', value: 'Allow' },
      component: ({ field }: CustomComponentProps) => (
        <textarea
          {...field}
          rows={4}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
        />
      ),
    },
    {
      name: 'idProof',
      label: 'Id Proof',
      type: 'file',
      isRequired: true,
      component: () => (
        <ImageUploadField
          name="idProof"
          label="idProof"
        />
      )
    },
    {
      name: 'restaurantLicense',
      label: 'Restaurant License',
      type: 'file',
      isRequired: true,
      component: () => (
        <ImageUploadField
          name="restaurantLicense"
          label="idProof"
        />
      )
    },
    {
      name: 'stripeSection',
      type: 'section',
      sectionTitle: 'Stripe',
      sectionLine: true,
    },
    {
      name: 'stripePublishStatus',
      label: 'Publish Status',
      type: 'radio',
      radioOptions: ['publish', 'unpublish'],
    },
    {
      name: 'stripeClientId',
      label: 'Client ID',
      type: 'text',
    },
    {
      name: 'stripeSecretId',
      label: 'Secret ID',
      type: 'text',
    },
    {
      name: 'paypalSection',
      type: 'section',
      sectionTitle: 'Paypal',
      sectionLine: true,
    },
    {
      name: 'paypalPublishStatus',
      label: 'Publish Status',
      type: 'radio',
      radioOptions: ['publish', 'unpublish'],
    },
    {
      name: 'paypalClientId',
      label: 'Client ID',
      type: 'text',
    },
    {
      name: 'paypalSecretId',
      label: 'Secret ID',
      type: 'text',
    },
    {
      name: 'netBankingSection',
      type: 'section',
      sectionTitle: 'Net Banking',
      sectionLine: true,
    },
    {
      name: 'netBankingPublishStatus',
      label: 'Publish Status',
      type: 'radio',
      radioOptions: ['publish', 'unpublish'],
    },
    {
      name: 'bankName',
      label: 'Bank Name',
      type: 'text',
    },
    {
      name: 'branch',
      label: 'Branch',
      type: 'text',
    },
    {
      name: 'accountNo',
      label: 'Account No.',
      type: 'number',
    },
    {
      name: 'ifscCode',
      label: 'IFSC Code',
      type: 'text',
    },
    {
      name: 'addressSection',
      type: 'section',
      sectionTitle: 'Address',
      sectionLine: true,
    },
    // {
    //   name: 'location',
    //   label: 'Location',
    //   type: 'custom',
    //   isRequired: true,
    //   component: LocationSearchInputWrapper,
    // },
     {
      name: 'location',
      label: 'Location',
      type: 'custom',
      isRequired: true,
      // component: LocationSearchInputWrapper,
      component: ({ field, form }: any) => {
        const handleSearchLocation = (data: {
          latLng: { lat: number; lng: number };
          city: string;
          state: string;
          country: string;
        }) => {
          const cityValue = data.city && data.city.trim() ? data.city : data.state;
          const locationString = `${cityValue}, ${data.state}, ${data.country}`;
          form.setFieldValue(field.name, locationString);
          form.setFieldTouched(field.name, false);
          form.setFieldValue('city', cityValue);
          form.setFieldTouched('city', false);
          form.setFieldValue('state', data.state);
          form.setFieldTouched('state', false);
          form.setFieldValue('country', data.country);
          form.setFieldTouched('country', false);
          setTimeout(() => {
            form.validateForm();
          }, 0);
        };

        const hasError =
          form.touched[field.name] && Boolean(form.errors[field.name]);

        return (
          <Box>
            <LocationSearchInput
              // key={inputKey} // re-initialize component on reset
              handlesearchLocation={handleSearchLocation}
              address={address}
              // setAddress={setAddress}
              setAddress={(val) => {
                setAddress(val);
                form.setFieldValue(field.name, val);
                form.setFieldTouched(field.name, true, false); // Always mark as touched on change/erase
              }}
            />
            {hasError && (
              <FormHelperText error>{form.errors[field.name]}</FormHelperText>
            )}
          </Box>
        );
      },
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      isRequired: true,
      disabled: true,
    },
    {
      name: 'state',
      label: 'State',
      type: 'text',
      isRequired: true,
      disabled: true,
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      isRequired: true,
      disabled: true,
    },
  ];

  const filteredFields = storeOwner
    ? allFields.filter(
      (field) => field.name !== 'password' && field.name !== 'confirmPassword'
    )
    : allFields;

  const formConfig = {
    initialValues,
    validationSchema,
    onSubmit: (values: Record<string, any>) => {
      const formValues = values as FormValues;
      return handleSubmit(formValues);
    },
    fields: filteredFields,
    customButton: ({ handleSubmit }: CustomButtonProps) => (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          disabled={isPending}
          sx={{ bgcolor: primary.main }}
          onClick={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button
          variant="contained"
          sx={{ bgcolor: warning.main }}
          onClick={() => (onClose ? onClose() : navigate(-1))}
          type="button"
        >
          Cancel
        </Button>
      </Box>
    ),
  };

  return allForm(formConfig)();
};

export default EditStoreOwner;
